import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import type { GridKey } from "@appretio/shared";
import { parseVersionedPayload } from "../savedViews/payloadCodec";

/** Payload schema version; bumped only by a shape change that needs migrating. */
export const CONTEXT_VIEW_VERSION = 1;

/**
 * The one saved-view list every cube page reads its context views from. The
 * context dimensions are the same on forecast, budget and both summaries, so
 * a context saved on one page is offered on all of them.
 */
export const CONTEXT_VIEWS_GRID: GridKey = "context";

/** What the context bar selects: the dimension filters and the store statuses. */
export interface ContextSelection {
  context: ForecastContextSelectedOptions;
  storeStatuses: string[];
}

/**
 * A context view's payload, the whole of ``control_state``. Applying it
 * selects and stops: pressing Apply stays the planner's call, so a view
 * never loads data behind their back.
 */
export interface ContextViewPayload extends ContextSelection {
  version: number;
}

export function parseContextViewPayload(
  controlState: string | null | undefined
): ContextViewPayload | null {
  return parseVersionedPayload(controlState, CONTEXT_VIEW_VERSION);
}

/** Serialize a selection for the ``control_state`` column. */
export function serializeContextViewPayload(
  selection: ContextSelection
): string {
  return JSON.stringify({
    version: CONTEXT_VIEW_VERSION,
    context: selection.context,
    storeStatuses: selection.storeStatuses
  } satisfies ContextViewPayload);
}

/** The fields of a selection that hold a value, ordered, values sorted. */
export function selectedContextFields(
  context: ForecastContextSelectedOptions
): [string, string[]][] {
  return Object.entries(context ?? {})
    .filter(([, values]) => values?.length)
    .map(([field, values]): [string, string[]] => [field, [...values].sort()])
    .sort(([a], [b]) => a.localeCompare(b));
}

/** Whether two selections pick the same values, order and empties aside. */
export function sameContextSelection(
  a: ContextSelection,
  b: ContextSelection
): boolean {
  return (
    JSON.stringify(selectedContextFields(a.context)) ===
      JSON.stringify(selectedContextFields(b.context)) &&
    JSON.stringify([...a.storeStatuses].sort()) ===
      JSON.stringify([...b.storeStatuses].sort())
  );
}
